import { api } from './api';
import type { ProjectStatus } from './projectStatus';
import { uploadFile } from './uploads';

/**
 * Mirrors the server's `toProjectResponse`. There is no shared types package, so
 * this is hand-kept in sync the same way `User` in lib/auth.tsx is.
 */
export type Project = {
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  status: ProjectStatus;
  pinned: boolean;
  /** Same-origin path, not a signed URL -- it never expires. */
  imageUrl: string | null;
  createdBy: { id: string; email: string; name: string | null; pictureUrl: string | null } | null;
  createdAt: string;
  updatedAt: string;
};

export type ProjectInput = {
  name: string;
  description: string | null;
  tags: string[];
  status: ProjectStatus;
  pinned: boolean;
  imageKey?: string | null;
};

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function listProjects(
  filters: { status?: ProjectStatus[]; pinned?: boolean },
  signal?: AbortSignal,
): Promise<{ projects: Project[] }> {
  const params = new URLSearchParams();
  if (filters.status?.length) params.set('status', filters.status.join(','));
  // Only ever narrows to the pinned ones; omitted means "all".
  if (filters.pinned) params.set('pinned', 'true');
  const query = params.toString();
  return api.get(`/api/projects${query ? `?${query}` : ''}`, signal);
}

export const getProject = (id: string, signal?: AbortSignal) =>
  api.get<{ project: Project }>(`/api/projects/${id}`, signal);

export const listTags = (signal?: AbortSignal) =>
  api.get<{ tags: string[] }>('/api/projects/tags', signal);

export const createProject = (input: ProjectInput) =>
  api.post<{ project: Project }>('/api/projects', input);

export const updateProject = (id: string, input: Partial<ProjectInput>) =>
  api.patch<{ project: Project }>(`/api/projects/${id}`, input);

export const deleteProject = (id: string) => api.del<{ ok: true }>(`/api/projects/${id}`);

/**
 * Mirrors the server's `orderBy` so re-sorting after a pin toggle lands the
 * project exactly where a refetch would put it.
 */
export function byPinnedThenUpdated(a: Project, b: Project): number {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
  return b.updatedAt.localeCompare(a.updatedAt);
}

/**
 * The image-shaped view of the generic uploader: the project form only ever
 * needs the key back.
 */
export const uploadImage = (file: File): Promise<string> =>
  uploadFile(file, 'projectImage').then((upload) => upload.key);
